import { supabase } from './supabase'

/**
 * Custom email verification that works around Supabase verification issues
 */
export const verifyEmailCode = async (email: string, code: string) => {
  try {
    // Normalize email to lowercase and trim
    const normalizedEmail = email.trim().toLowerCase()
    const normalizedCode = code.trim()
    
    // First try the built-in Supabase verification
    // Note: 'email' is the correct type for email OTPs generated by signUp()
    console.log('[VERIFY] Attempting verification:', {
      email: normalizedEmail,
      codeLength: normalizedCode.length,
      type: 'email'
    })

    const { data, error } = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: normalizedCode,
      type: 'email'
    })

    if (!error) {
      console.log('[VERIFY] Built-in verification succeeded')
      return { success: true, data }
    }

    // If built-in fails, try our Edge Function as fallback
    console.error('[VERIFY] Built-in verification failed:', {
      message: error.message,
      status: error.status,
      code: error.code,
      name: error.name
    })
    
    // Get current session token if available, otherwise use anon key
    const { data: { session } } = await supabase.auth.getSession()
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    const authToken = session?.access_token || supabaseAnonKey
    
    const functionUrl = `${supabaseUrl}/functions/v1/verify`
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey
      },
      body: JSON.stringify({
        email: normalizedEmail,
        token: normalizedCode,
        type: 'email'
      })
    })

    const result = await response.json()

    if (!response.ok) {
      console.error('[VERIFY] Edge Function failed:', {
        status: response.status,
        error: result.error,
        result
      })
      throw new Error(result.error || 'Verification failed')
    }

    console.log('[VERIFY] Edge Function succeeded')
    return { success: true, data: result }

  } catch (error: any) {
    console.error('[VERIFY] Complete verification failure:', {
      message: error.message,
      error
    })
    throw error
  }
}

/**
 * Resend verification code
 */
export const resendVerificationCode = async (email: string, userData?: { full_name?: string; first_name?: string; last_name?: string; fullName?: string }) => {
  const normalizedEmail = email.trim().toLowerCase()
  
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: normalizedEmail,
    options: userData ? {
      data: userData
    } : undefined
  })

  if (error) {
    throw error
  }
}